<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Directory"
      description="ASCDI Members can access an ENHANCED searchable database of members and non-members which includes company information, contact information, products sold and a whole lot more."
      :mode="mode"
    />
    <AppCard :mode="mode">
      <template #body>
        <div class="mb-6 grid gap-6">
          <div class="relative flex items-center">
            <Search
              :size="20"
              :stroke-width="1.75"
              class="absolute left-4 opacity-40"
            />
            <AppInput
              :mode="mode"
              type="search"
              placeholder="Search by company name"
              v-model="memberSearch.company_name"
              classes="!pl-11"
              :hasLabel="false"
              @keyup.enter="search"
            />
          </div>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-4">
            <AppSelect
              :mode="mode"
              title="Product categories"
              v-model="memberSearch.product_categories"
            >
              <template #options>
                <option value="">Product category</option>
                <option
                  v-for="(category, key) in productCategories"
                  :value="category.category_name"
                  :key="key"
                >
                  {{ category.category_name }}
                </option>
              </template>
            </AppSelect>
            <AppSelect
              :mode="mode"
              title="Specialties"
              v-model="memberSearch.specialties"
            >
              <template #options>
                <option value="">Specialty</option>
                <option
                  v-for="(specialty, key) in specialties"
                  :value="specialty.specialty_name"
                  :key="key"
                >
                  {{ specialty.specialty_name }}
                </option>
              </template>
            </AppSelect>
          </div>
          <AppButton
            title="Search"
            @action="search"
            :isDisabled="!hasSearchCriteria"
          />
        </div>
        <template v-if="paginatedItems.length > 0">
          <div class="mb-4 hidden xl:block">
            <AppSelect
              :mode="mode"
              title="Sort by"
              :modelValue="mobileSort"
              @update:modelValue="applyMobileSort"
            >
              <template #options>
                <option value="">Default (best match)</option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:asc`"
                  :value="`${option.sortKey}:asc`"
                >
                  {{ option.name }} (A-Z)
                </option>
                <option
                  v-for="option in sortableColumns"
                  :key="`${option.sortKey}:desc`"
                  :value="`${option.sortKey}:desc`"
                >
                  {{ option.name }} (Z-A)
                </option>
              </template>
            </AppSelect>
          </div>
          <AppTable
            :mode="mode"
            :columns="columns"
            :sortKey="sort.key"
            :sortOrder="sort.order"
            @sort="toggleSort"
          >
            <template #rows>
              <tr v-for="(member, key) in paginatedItems" :key="key">
                <td>
                  <FallbackText
                    :value="member.CompanyName"
                    :isTruncate="false"
                  />
                </td>
                <td>
                  <Country
                    :mode="mode"
                    :country="member.Country"
                    :countries="countries"
                  />
                </td>
                <td>
                  <FallbackText :value="member.City" :isTruncate="false" />
                </td>
                <td>
                  <FallbackText :value="member.State" :isTruncate="false" />
                </td>
                <td>
                  <FallbackText :value="member.URL" :isTruncate="false" />
                </td>
                <td>
                  <div class="flex items-center justify-center gap-2">
                    <TableAction
                      :mode="mode"
                      @itemAction="
                        $redirectTo('member-details', member.MemberID)
                      "
                      action="view"
                    />
                  </div>
                </td>
              </tr>
            </template>
          </AppTable>
          <MobileTable
            :mode="mode"
            :fields="{
              company: 'Company',
              country: 'Country',
              city: 'City',
              state: 'State',
              url: 'URL',
              actions: 'Actions',
            }"
            :data="paginatedItems"
          >
            <template #default="{ row, fieldKey }">
              <template v-if="fieldKey === 'company'">
                <FallbackText :value="row.CompanyName" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'country'">
                <Country
                  :mode="mode"
                  :country="row.Country"
                  :countries="countries"
                />
              </template>
              <template v-else-if="fieldKey === 'city'">
                <FallbackText :value="row.City" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'state'">
                <FallbackText :value="row.State" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'url'">
                <FallbackText :value="row.URL" :isTruncate="false" />
              </template>
              <template v-else-if="fieldKey === 'actions'">
                <div class="flex items-center gap-2">
                  <TableAction
                    :mode="mode"
                    @itemAction="$redirectTo('member-details', row.MemberID)"
                    action="view"
                  />
                </div>
              </template>
            </template>
          </MobileTable>
        </template>
        <EmptyData v-else :notFound="'No members represented!'" :mode="mode" />
        <AppPagination
          :mode="mode"
          :key="`${sort.key}:${sort.order}`"
          :data="sortedItems"
          @pageChanged="updatePaginatedItems"
        />
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import { Search } from "lucide-vue-next";
import Section from "@C/Common/Section.vue";
import tableSort from "@L/tableSort";
import AppInput from "@C/Form/AppInput.vue";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import AppSelect from "@C/Form/AppSelect.vue";
import AppCard from "@C/Common/AppCard.vue";
import AuthLayout from "@A/AuthLayout.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppTable from "@C/Tables/AppTable.vue";
import FallbackText from "@C/Tables/FallbackText.vue";
import TableAction from "@C/Tables/TableAction.vue";
import MobileTable from "@C/Tables/MobileTable.vue";
import AppPagination from "@C/Common/AppPagination.vue";
import EmptyData from "@C/Common/EmptyData.vue";
import Country from "@C/Tables/Country.vue";

export default {
  name: "MemberDirectory",
  mixins: [tableSort],
  components: {
    AuthLayout,
    Section,
    Search,
    AppInput,
    AppSelect,
    AppCard,
    AppButton,
    AppTable,
    FallbackText,
    TableAction,
    MobileTable,
    AppPagination,
    EmptyData,
    Country,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      memberSearch: {
        company_name: "",
        specialties: "",
        product_categories: "",
      },
      productCategories: [],
      specialties: [],
      members: [],
      columns: [
        {
          name: "Company",
          sortKey: "CompanyName",
          classes: "min-w-[280px]",
        },
        {
          name: "Country",
          sortKey: "Country",
        },
        {
          name: "City",
          sortKey: "City",
        },
        {
          name: "State",
          sortKey: "State",
        },
        {
          name: "URL",
          sortKey: "URL",
        },
        {
          name: "Actions",
          classes: "w-[80px]",
        },
      ],
      paginatedItems: [],
    };
  },
  mounted() {
    this.getData("categories");
    this.getData("specialties");
  },
  methods: {
    async getData(type) {
      try {
        const response = await fetch(
          `/public_api/${type === "categories" ? "product_categories" : "specialties"}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        if (!response.ok) {
          this.toastStore.show(
            `Failed to fetch ${type === "categories" ? "product categories" : "specialties"}`,
            "error",
          );
          return;
        }
        const data = await response.json();
        if (!data) {
          this.toastStore.show(
            `No ${type === "categories" ? "product categories" : "specialties"} found`,
            "error",
          );
          return;
        }
        if (type === "categories") {
          this.productCategories = data;
        } else {
          this.specialties = data;
        }
      } catch (e) {
        this.handleError(e);
      }
    },
    async search() {
      this.loaderStore.setIsLoading(true);
      try {
        const response = await fetch(`/public_api/members_search`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(this.memberSearch),
        });
        if (!response.ok) {
          this.members = [];
          return;
        }
        const data = await response.json();
        if (!data) {
          this.members = [];
          return;
        }
        this.members = data;
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    sortValue(row, key) {
      // The public endpoint returns flattened records (CompanyName, City, ...),
      // not Elastic _source documents, so the keys are the column keys directly.
      return row[key];
    },
    updatePaginatedItems(paginatedData) {
      this.paginatedItems = paginatedData;
    },
  },
  computed: {
    sortSource() {
      return this.members;
    },
    hasSearchCriteria() {
      // Any one criterion is enough.  This used to require both dropdowns, which
      // would have left the company-name box unusable on its own.
      return Boolean(
        this.memberSearch.company_name ||
          this.memberSearch.specialties ||
          this.memberSearch.product_categories,
      );
    },
    countries() {
      return this.countriesStore.countries;
    },
  },
};
</script>
